import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ApiRequestStatus } from '../../../model/api-request';
import { GlobalActions } from '../../../model/global-actions';
import {
  changePassword,
  resetChangePasswordState
} from '../../../store/features/profile/change-password/change-password-slice';
import { messagesForChangePasswordError } from '../../../store/features/profile/change-password/change-password-utils';
import { useAppDispatch, useAppSelector } from '../../redux';

export interface ChangePasswordLogicCallbacks {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  changePasswordRequestStatus: ApiRequestStatus;
  changePasswordForm: boolean;
  openSuccessDialog: boolean;

  onChangePasswordForm: () => void;
  handleCloseSuccessDialog: () => void;
  setOldPassword: (string) => void;
  setNewPassword: (string) => void;
  setConfirmNewPassword: (string) => void;
  handleSubmit: () => void;
}

export const useChangePassword = (
  actions: GlobalActions
): ChangePasswordLogicCallbacks => {
  const { showToast, closeToast } = actions;

  const [oldPassword, setOldPassword] = useState<string>();
  const [newPassword, setNewPassword] = useState<string>();
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>();

  const [changePasswordForm, setChangePasswordForm] = useState(false);

  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  const { changePasswordRequestStatus, changePasswordError } = useAppSelector(
    (state) => state.features.changePassword
  );

  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();

  const handleSubmit = useCallback(() => {
    dispatch(
      changePassword({
        oldPassword: oldPassword,
        newPassword: newPassword,
        newPasswordConfirm: confirmNewPassword,
      })
    );
  }, [oldPassword, newPassword, confirmNewPassword, dispatch]);

  const handleCloseSuccessDialog = useCallback(() => {
    setOpenSuccessDialog(false);
  }, [setOpenSuccessDialog]);

  useEffect(() => {
    if (changePasswordError) {
      const errorMessages = messagesForChangePasswordError(changePasswordError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(resetChangePasswordState());
    }
  }, [changePasswordError, closeToast, dispatch, formatMessage, showToast]);

  useEffect(() => {
    if (changePasswordRequestStatus === 'succeeded') {
      setOpenSuccessDialog(true);
      setChangePasswordForm(false);
      dispatch(resetChangePasswordState());
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    }
  }, [
    changePasswordRequestStatus,
    setOpenSuccessDialog,
    setChangePasswordForm,
    dispatch,
  ]);

  const onChangePasswordForm = useCallback(() => {
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    return !changePasswordForm
      ? setChangePasswordForm(true)
      : setChangePasswordForm(false);
  }, [changePasswordForm, setChangePasswordForm]);

  return {
    oldPassword,
    newPassword,
    confirmNewPassword,
    changePasswordRequestStatus,
    openSuccessDialog,
    changePasswordForm,
    handleCloseSuccessDialog,
    onChangePasswordForm,
    setConfirmNewPassword,
    setNewPassword,
    setOldPassword,
    handleSubmit,
  };
};
